// Cookies d'auth écrits côté client et relus par le serveur pour les redirections SSR du tunnel SEA.
// Non-httpOnly (comme l'ancien sessionStorage), SameSite=Lax, Secure hors dev, cookies de session.

const AUTH_TEMP_TOKEN_COOKIE: string = 'prepeers_temp_token'
const AUTH_HAS_PHONE_COOKIE: string = 'prepeers_has_phone'
const AUTH_HAS_ACCOUNT_COOKIE: string = 'prepeers_has_account'
const TUNNEL_SESSION_STORAGE_PREFIX: string = 'prepeers:tunnel:'

/**
 * Écrit un cookie de session côté client.
 * @param {string} name - Nom du cookie.
 * @param {string} value - Valeur (encodée en URI).
 * @returns {void}
 */
function writeCookie(name: string, value: string): void {
  if (!import.meta.client) return
  const attributes: string[] = [`${name}=${encodeURIComponent(value)}`, 'path=/', 'SameSite=Lax']
  if (!import.meta.dev) attributes.push('Secure')
  document.cookie = attributes.join('; ')
}

/**
 * Supprime un cookie côté client.
 * @param {string} name - Nom du cookie.
 * @returns {void}
 */
function deleteCookie(name: string): void {
  if (!import.meta.client) return
  const attributes: string[] = [`${name}=`, 'path=/', 'max-age=0', 'SameSite=Lax']
  if (!import.meta.dev) attributes.push('Secure')
  document.cookie = attributes.join('; ')
}

/**
 * Lit la valeur d'un cookie côté client.
 * @param {string} name - Nom du cookie.
 * @returns {string | null} Valeur décodée, ou `null` si absent ou hors client.
 */
function readCookie(name: string): string | null {
  if (!import.meta.client) return null
  const prefix: string = `${name}=`
  const match: string | undefined = document.cookie
    .split('; ')
    .find((row: string) => row.startsWith(prefix))
  return match === undefined ? null : decodeURIComponent(match.slice(prefix.length))
}

/**
 * Supprime les brouillons du tunnel SEA (`prepeers:tunnel:*`) en sessionStorage.
 * @returns {void}
 */
function clearTunnelSessionStorage(): void {
  if (!import.meta.client) return
  const keysToRemove: string[] = []
  for (let index: number = 0; index < window.sessionStorage.length; index += 1) {
    const key: string | null = window.sessionStorage.key(index)
    if (key !== null && key.startsWith(TUNNEL_SESSION_STORAGE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    window.sessionStorage.removeItem(key)
  }
}

/** Lecture/écriture des cookies d'auth (réutilisable hors tunnel). */
export const authCookies = {
  /**
   * Persiste le JWT temp user (multi-onglets, survit au refresh).
   * @param {string} token - JWT `temp_session`.
   * @returns {void}
   */
  setTempToken(token: string): void {
    writeCookie(AUTH_TEMP_TOKEN_COOKIE, token)
  },

  /**
   * Lit le JWT temp user.
   * @returns {string | null} Token, ou `null` si absent.
   */
  getTempToken(): string | null {
    return readCookie(AUTH_TEMP_TOKEN_COOKIE)
  },

  /**
   * Efface le JWT temp user.
   * @returns {void}
   */
  clearTempToken(): void {
    deleteCookie(AUTH_TEMP_TOKEN_COOKIE)
  },

  /**
   * Mémorise si un téléphone est renseigné (le serveur peut alors router vers `/discuter`).
   * @param {boolean} hasPhone - `true` si un numéro est renseigné.
   * @returns {void}
   */
  setHasPhone(hasPhone: boolean): void {
    writeCookie(AUTH_HAS_PHONE_COOKIE, hasPhone ? '1' : '0')
  },

  /**
   * Mémorise qu'un compte Entra (MSAL) est connecté : le serveur sait alors que l'utilisateur est
   * connecté même si le token MSAL n'est pas accessible dans un cookie.
   * @param {boolean} hasAccount - `true` si un compte Entra est connecté.
   * @returns {void}
   */
  setHasAccount(hasAccount: boolean): void {
    writeCookie(AUTH_HAS_ACCOUNT_COOKIE, hasAccount ? '1' : '0')
  },

  /**
   * Indique si un compte Entra (MSAL) est connecté (cookie posé au login).
   * @returns {boolean} `true` si un compte est connecté.
   */
  hasAccount(): boolean {
    return readCookie(AUTH_HAS_ACCOUNT_COOKIE) === '1'
  },

  /**
   * Efface tous les cookies d'auth et le brouillon tunnel SEA (déconnexion).
   * @returns {void}
   */
  clearAll(): void {
    deleteCookie(AUTH_TEMP_TOKEN_COOKIE)
    deleteCookie(AUTH_HAS_PHONE_COOKIE)
    deleteCookie(AUTH_HAS_ACCOUNT_COOKIE)
    clearTunnelSessionStorage()
  },
}
